import { SugarMoneyApiEnvironmentsEnum, WebsocketUrls } from '../../constants';
import {
  SugarMoneyWebSocketErrorHandler,
  SugarMoneyWebSocketConnectionHandler,
  SugarMoneyWebSocketOptions,
  SugarMoneyUniversalWebSocketHandler,
  SugarMoneyWebSocketEventType,
  SugarMoneyWebSocketConnectionState,
  SugarMoneyWebSocketMessage,
  SugarMoneyWebSocketEventData,
} from '../../types/websocket';

export class SugarMoneyWebSocketConnection {
  private ws: WebSocket | null = null;
  private environment: SugarMoneyApiEnvironmentsEnum;
  private options: SugarMoneyWebSocketOptions;
  private errorHandler: SugarMoneyWebSocketErrorHandler | null = null;
  private connectionHandler: SugarMoneyWebSocketConnectionHandler | null = null;
  private disconnectionHandler: SugarMoneyWebSocketConnectionHandler | null = null;
  private universalHandlers: SugarMoneyUniversalWebSocketHandler[] = [];
  private reconnectAttempts = 0;
  private reconnectTimeout: number | null = null;
  private state: SugarMoneyWebSocketConnectionState = 'disconnected';

  constructor({
    environment,
    options = {},
  }: {
    environment: SugarMoneyApiEnvironmentsEnum;
    options?: SugarMoneyWebSocketOptions;
  }) {
    this.environment = environment;
    this.options = {
      autoReconnect: true,
      reconnectInterval: 5000,
      maxReconnectAttempts: 5,
      ...options,
    };
  }

  /**
   * Connect to the WebSocket server
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.state = 'connecting';
        const wsUrl = WebsocketUrls[this.environment];

        console.log('Connecting to WebSocket:', wsUrl);

        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          this.state = 'connected';
          this.reconnectAttempts = 0;
          if (this.connectionHandler) {
            this.connectionHandler();
          }
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: SugarMoneyWebSocketMessage<SugarMoneyWebSocketEventData> = JSON.parse(
              event.data,
            );
            this.handleMessage(message);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
            if (this.errorHandler) {
              this.errorHandler(error as Error);
            }
          }
        };

        this.ws.onerror = (error) => {
          this.state = 'error';
          if (this.errorHandler) {
            this.errorHandler(new Error('WebSocket error'));
          }
          reject(new Error('WebSocket connection failed'));
        };

        this.ws.onclose = () => {
          this.state = 'disconnected';
          if (this.disconnectionHandler) {
            this.disconnectionHandler();
          }

          if (
            this.options.autoReconnect &&
            this.reconnectAttempts < this.options.maxReconnectAttempts!
          ) {
            this.scheduleReconnect();
          }
        };
      } catch (error) {
        this.state = 'error';
        reject(error);
      }
    });
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.state = 'disconnected';
  }

  /**
   * Get the current connection state
   */
  getState(): SugarMoneyWebSocketConnectionState {
    return this.state;
  }

  /**
   * Check if the connection is established
   */
  isConnected(): boolean {
    return this.state === 'connected' && this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Subscribe to connection events
   */
  onConnection(handler: SugarMoneyWebSocketConnectionHandler): void {
    this.connectionHandler = handler;
  }

  /**
   * Subscribe to disconnection events
   */
  onDisconnection(handler: SugarMoneyWebSocketConnectionHandler): void {
    this.disconnectionHandler = handler;
  }

  /**
   * Subscribe to error events
   */
  onError(handler: SugarMoneyWebSocketErrorHandler): void {
    this.errorHandler = handler;
  }

  /**
   * Subscribe to ALL WebSocket messages with a universal handler
   */
  onAllMessages(handler: SugarMoneyUniversalWebSocketHandler): void {
    this.universalHandlers.push(handler);
  }

  /**
   * Remove a universal message handler
   */
  offAllMessages(handler: SugarMoneyUniversalWebSocketHandler): void {
    const index = this.universalHandlers.indexOf(handler);
    if (index > -1) {
      this.universalHandlers.splice(index, 1);
    }
  }

  /**
   * Handle incoming messages
   */
  private handleMessage(message: SugarMoneyWebSocketMessage<SugarMoneyWebSocketEventData>): void {
    // Call universal handlers with the complete message
    this.universalHandlers.forEach((handler) =>
      handler({
        type: message.type as SugarMoneyWebSocketEventType,
        data: message.data,
      }),
    );
  }

  /**
   * Schedule a reconnection attempt
   */
  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    this.reconnectTimeout = setTimeout(() => {
      this.connect().catch((error) => {
        console.error('Reconnection failed:', error);
      });
    }, this.options.reconnectInterval) as any;
  }
}

export const websocketModule = ({
  environment,
}: {
  environment: SugarMoneyApiEnvironmentsEnum;
}) => {
  let connection: SugarMoneyWebSocketConnection | null = null;

  return {
    /**
     * Get the current connection instance
     */
    getConnection: () => connection,

    /**
     * Quick connect with default options
     */
    connect: async (options?: SugarMoneyWebSocketOptions) => {
      if (!connection) {
        connection = new SugarMoneyWebSocketConnection({
          environment,
          options,
        });
      }

      await connection.connect();
      return connection;
    },

    /**
     * Disconnect the current connection
     */
    disconnect: () => {
      if (connection) {
        connection.disconnect();
        connection = null;
      }
    },

    /**
     * Subscribe to ALL WebSocket events with a single handler
     * Receives raw WebSocket messages directly without individual event parsing
     * @param handler - Function that receives the complete WebSocket message with proper typing
     */
    subscribeToAll: (handler: SugarMoneyUniversalWebSocketHandler) => {
      if (!connection) {
        throw new Error('No WebSocket connection established. Call connect() first.');
      }

      connection.onAllMessages(handler);
    },

    /**
     * Unsubscribe from ALL WebSocket events
     * @param handler - The same handler function that was used to subscribe
     */
    unsubscribeFromAll: (handler: SugarMoneyUniversalWebSocketHandler) => {
      if (!connection) {
        throw new Error('No WebSocket connection established. Call connect() first.');
      }

      connection.offAllMessages(handler);
    },
  };
};
